import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Doughnut, Pie } from 'react-chartjs-2';
import { resultsService, FilterResultsDto } from '../services/results.service';
import { pdfJobsService } from '../services/pdfJobs.service';
import AptDetailTable from '../components/apt/AptDetailTable';
import AptCompareView from '../components/apt/AptCompareView';
import './Dashboard.css';

type AptTab = 'dashboard' | 'detail' | 'compare';
type SpamSortKey = 'company' | 'team' | 'name' | 'category';
type SpamSortDir = 'asc' | 'desc';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

interface DashboardContext {
  selectedRound: string | null;
}

// PDF 모드: Puppeteer가 ?pdf=true&round=&period=&company=&team=&tab= 로 접속
const pdfUrlParams = new URLSearchParams(window.location.search);
const IS_PDF_MODE = pdfUrlParams.get('pdf') === 'true';
const PDF_TAB = pdfUrlParams.get('tab'); // 'compare'면 데이터 비교 탭 캡처

const Dashboard: React.FC = () => {
  const { selectedRound } = useOutletContext<DashboardContext>();
  const [filters, setFilters] = useState<FilterResultsDto>(
    IS_PDF_MODE
      ? {
          round: pdfUrlParams.get('round') || undefined,
          period: pdfUrlParams.get('period') || undefined,
          company: pdfUrlParams.get('company') || undefined,
          team: pdfUrlParams.get('team') || undefined,
        }
      : {
          round: selectedRound || undefined,
          period: undefined,
          company: undefined,
          team: undefined,
        },
  );
  const [activeTab, setActiveTab] = useState<AptTab>(
    IS_PDF_MODE && PDF_TAB === 'compare' ? 'compare' : 'dashboard',
  );
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  // 스팸신고자 표 정렬
  const [spamSortKey, setSpamSortKey] = useState<SpamSortKey>('company');
  const [spamSortDir, setSpamSortDir] = useState<SpamSortDir>('asc');
  const [stats, setStats] = useState<any>(null);
  const [availableCompanies, setAvailableCompanies] = useState<string[]>([]);
  const [availableTeams, setAvailableTeams] = useState<string[]>([]);
  const [availableRounds, setAvailableRounds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [spamReporters, setSpamReporters] = useState<any[]>([]);
  const [isLoadingSpamReporters, setIsLoadingSpamReporters] = useState(false);

  useEffect(() => {
    // PDF 모드는 드롭다운을 렌더하지 않으므로 회차/회사 목록 조회 불필요
    if (IS_PDF_MODE) return;
    loadRounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadStats();
    loadSpamReporters();
    // 회사 드롭다운 목록은 PDF 모드에서 불필요
    if (!IS_PDF_MODE) loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    // PDF 모드: 팀 드롭다운 미사용 + URL로 받은 team 유지 (초기화/조회 모두 스킵)
    if (IS_PDF_MODE) return;
    if (!filters.company) {
      setAvailableTeams([]);
      // 회사 미선택: 기간 미사용(round로 전체 조회)
      setFilters((prev) =>
        prev.team === undefined && prev.period === undefined ? prev : { ...prev, team: undefined, period: undefined },
      );
      return;
    }
    // 회사 선택: (회차 + 회사) 기준 기간 주입 — 같은 회차라도 회사별 기간이 다를 수 있음
    // 팀 구성은 기간마다 달라지므로 팀 목록은 주입된 기간으로 조회한다
    const co = filters.company;
    const ro = filters.round;
    let stale = false;
    resultsService
      .getAvailablePeriods(ro, co)
      .then(async (periods) => {
        const p = periods[0] || undefined;
        if (stale) return;
        setFilters((prev) =>
          prev.company === co && prev.round === ro && prev.period !== p ? { ...prev, period: p } : prev,
        );
        const teams = await resultsService.getAvailableTeams(ro || undefined, co, p);
        if (!stale) setAvailableTeams(teams);
      })
      .catch((error) => console.error('Failed to load periods/teams:', error));
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.company, filters.round]);

  // PDF 모드: Puppeteer가 데이터 로딩 완료를 감지할 수 있도록 window 신호 노출
  // (데이터 비교 탭 PDF는 AptCompareView가 자체적으로 신호를 담당)
  useEffect(() => {
    if (!IS_PDF_MODE || PDF_TAB === 'compare') return;
    const ready = !isLoading && !isLoadingSpamReporters && stats !== null;
    (window as any).__pdfIsLoading = !ready;
    (window as any).__pdfDataLoaded = ready;
  }, [isLoading, isLoadingSpamReporters, stats]);

  const loadRounds = async () => {
    try {
      const rounds = await resultsService.getAvailableRounds();
      setAvailableRounds(rounds);
      // 첫 번째 round를 기본값으로 설정 (회사 미선택이므로 period는 주입하지 않음 — round로 전체 조회)
      if (!filters.round && rounds.length > 0) {
        setFilters((prev) => ({ ...prev, round: rounds[0] }));
      }
    } catch (error) {
      console.error('Failed to load rounds:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const companies = await resultsService.getAvailableCompanies(
        filters.round || undefined,
        filters.period && filters.period !== 'all' ? filters.period : undefined
      );
      setAvailableCompanies(companies);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadStats = async () => {
    setIsLoading(true);
    try {
      const data = await resultsService.getStats(filters);
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSpamReporters = async () => {
    setIsLoadingSpamReporters(true);
    try {
      const data = await resultsService.getSpamReporters(filters);
      setSpamReporters(data);
    } catch (error) {
      console.error('Failed to load spam reporters:', error);
      setSpamReporters([]);
    } finally {
      setIsLoadingSpamReporters(false);
    }
  };

  const handleFilterChange = (key: keyof FilterResultsDto, value: string | undefined) => {
    if (key === 'round') {
      // 회차 변경: 회사·팀·기간 초기화. 회사 미선택 상태이므로 round로만 조회(기간 미사용).
      // 기간은 회사 선택 시 (회차+회사) 기준으로 주입된다.
      setFilters({ round: value, period: undefined, company: undefined, team: undefined });
    } else if (key === 'company') {
      setFilters({ ...filters, company: value, team: undefined });
    } else if (key === 'period') {
      setFilters({ ...filters, period: value });
    } else {
      setFilters({ ...filters, [key]: value });
    }
  };

  // 대시보드 화면 PDF 다운로드 (현재 필터 그대로 캡처 — NAC/DLP와 동일 방식)
  const handleDownloadPdf = async () => {
    if (!filters.round) {
      toast.error('회차를 선택해주세요.');
      return;
    }
    const round = filters.round;
    const company = filters.company || '전체';
    const toastId = toast.loading('PDF 작업 시작 중...');
    setIsDownloadingPdf(true);
    try {
      const { jobId } = await pdfJobsService.createJob('apt', filters.period || '', company, {
        round,
        team: filters.team,
      });
      toast.loading('PDF 생성 중... 0초 경과 (백그라운드 처리, 다른 메뉴로 이동해도 됩니다)', { id: toastId });

      const blob = await pdfJobsService.pollUntilDone(jobId, (elapsed) => {
        toast.loading(`PDF 생성 중... ${elapsed}초 경과 (백그라운드 처리, 다른 메뉴로 이동해도 됩니다)`, { id: toastId });
      });

      const scope = filters.company || '전체';
      const fileName = `[${scope}] 악성메일 모의 훈련 결과 (${round}).pdf`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF 다운로드 완료', { id: toastId });
    } catch (error: any) {
      console.error('PDF download failed:', error);
      const msg = error.response?.data?.message || error.message || 'PDF 생성에 실패했습니다.';
      toast.error('PDF 생성 실패: ' + msg, { id: toastId });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // 스팸신고자 정렬 (회사/팀/이름/카테고리)
  const handleSpamSort = (key: SpamSortKey) => {
    if (key === spamSortKey) {
      setSpamSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSpamSortKey(key);
      setSpamSortDir('asc');
    }
  };

  const sortedSpamReporters = React.useMemo(() => {
    const dir = spamSortDir === 'asc' ? 1 : -1;
    return [...spamReporters].sort((a, b) => {
      const av = (a[spamSortKey] ?? '') as string;
      const bv = (b[spamSortKey] ?? '') as string;
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return String(av).localeCompare(String(bv), 'ko') * dir;
    });
  }, [spamReporters, spamSortKey, spamSortDir]);

  const spamSortIndicator = (key: SpamSortKey) => {
    if (key !== spamSortKey) return ' ↕';
    return spamSortDir === 'asc' ? ' ▲' : ' ▼';
  };

  // 차트 제목 생성
  const getChartTitle = () => {
    const parts: string[] = [];
    
    if (filters.period && filters.period !== 'all') {
      parts.push(`${filters.period.slice(0, 4)}년 ${filters.period.slice(4)}월`);
    } else if (filters.period === 'all') {
      parts.push('전체 기간');
    }
    
    if (filters.round) {
      parts.push(filters.round);
    }
    
    if (filters.company && filters.company !== '') {
      parts.push(filters.company);
    }
    
    if (filters.team && filters.team !== '') {
      parts.push(filters.team);
    }
    
    return parts.length > 0 ? parts.join(' - ') : '전체';
  };

  // 제목 길이에 따라 폰트 크기 계산
  const getTitleFontSize = () => {
    const title = getChartTitle();
    const length = title.length;
    
    // 길이에 따라 폰트 크기 조정
    if (length > 30) {
      return '1rem';
    } else if (length > 20) {
      return '1.1rem';
    } else {
      return '1.2rem';
    }
  };

  // 정보입력 데이터가 있는지 확인
  const hasInfoInput = stats && stats.infoInput > 0;

  // 기본 stats 값 (데이터가 없을 때)
  const defaultStats = {
    total: 0,
    unread: 0,
    emailOpened: 0,
    linkClicked: 0,
    infoInput: 0,
    spamReport: 0,
    phishing: 0,
    spam: 0,
  };

  const currentStats = stats || defaultStats;

  // 막대 그래프용: 각 항목을 별도 데이터셋으로 구성, 스팸신고는 스택막대
  const barLabels = hasInfoInput 
    ? ['미열람', '메일열람', '링크클릭', '정보입력', '스팸신고']
    : ['미열람', '메일열람', '링크클릭', '스팸신고'];
  
  const barData = {
    labels: barLabels,
    datasets: [
      // 미열람 (퍼플 파스텔톤 - 채도 높임)
      {
        label: '미열람',
        data: hasInfoInput
          ? [currentStats.unread, 0, 0, 0, 0]
          : [currentStats.unread, 0, 0, 0],
        backgroundColor: 'rgba(156, 100, 200, 0.85)', // 진한 파스텔 퍼플
        borderColor: 'rgba(156, 100, 200, 1)',
        borderWidth: 1,
      },
      // 메일열람 (노란색 파스텔톤 - 채도 높임)
      {
        label: '메일열람',
        data: hasInfoInput
          ? [0, currentStats.emailOpened, 0, 0, 0]
          : [0, currentStats.emailOpened, 0, 0],
        backgroundColor: 'rgba(255, 220, 100, 0.85)', // 진한 파스텔 노란색
        borderColor: 'rgba(255, 220, 100, 1)',
        borderWidth: 1,
      },
      // 링크클릭 (적색 파스텔톤 - 채도 높임)
      {
        label: '링크클릭',
        data: hasInfoInput
          ? [0, 0, currentStats.linkClicked, 0, 0]
          : [0, 0, currentStats.linkClicked, 0],
        backgroundColor: 'rgba(255, 120, 120, 0.85)', // 진한 파스텔 적색
        borderColor: 'rgba(255, 120, 120, 1)',
        borderWidth: 1,
      },
      // 정보입력 (있는 경우만)
      ...(hasInfoInput ? [{
        label: '정보입력',
        data: [0, 0, 0, currentStats.infoInput, 0],
        backgroundColor: 'rgba(46, 204, 113, 0.85)',
        borderColor: 'rgba(46, 204, 113, 1)',
        borderWidth: 1,
      }] : []),
      // 스팸신고 스택막대 (피싱 - 녹색 파스텔톤 - 채도 높임)
      {
        label: '피싱',
        data: hasInfoInput
          ? [0, 0, 0, 0, currentStats.phishing]
          : [0, 0, 0, currentStats.phishing],
        backgroundColor: 'rgba(100, 200, 180, 0.85)', // 진한 파스텔 녹색
        borderColor: 'rgba(100, 200, 180, 1)',
        borderWidth: 1,
      },
      // 스팸신고 스택막대 (스팸 - 파란색 파스텔톤 - 채도 높임)
      {
        label: '스팸',
        data: hasInfoInput
          ? [0, 0, 0, 0, currentStats.spam]
          : [0, 0, 0, currentStats.spam],
        backgroundColor: 'rgba(120, 180, 240, 0.85)', // 진한 파스텔 파란색
        borderColor: 'rgba(120, 180, 240, 1)',
        borderWidth: 1,
      },
    ],
  };

  // 파이/도넛 차트용: 비율 표시를 위해 중복 제거 (메일열람만 = 전체 - 미열람 - 링크클릭 - 스팸신고)
  const emailOpenedOnly = Math.max(0, currentStats.emailOpened - currentStats.linkClicked - currentStats.spamReport);

  const pieLabels = hasInfoInput
    ? ['미열람', '메일열람만', '링크클릭', '스팸신고', '정보입력']
    : ['미열람', '메일열람만', '링크클릭', '스팸신고'];

  // 파이/도넛 차트 색상 (파스텔톤 - 채도 높임)
  const pieChartColors = {
    unread: 'rgba(156, 100, 200, 0.85)', // 미열람 - 진한 파스텔 퍼플
    emailOpened: 'rgba(255, 220, 100, 0.85)', // 메일열람만 - 진한 파스텔 노란색
    linkClicked: 'rgba(255, 120, 120, 0.85)', // 링크클릭 - 진한 파스텔 적색
    spamReport: 'rgba(120, 180, 240, 0.85)', // 스팸신고 - 진한 파스텔 파란색
    infoInput: 'rgba(46, 204, 113, 0.85)', // 정보입력
  };
  const pieChartBorderColors = {
    unread: 'rgba(156, 100, 200, 1)',
    emailOpened: 'rgba(255, 220, 100, 1)',
    linkClicked: 'rgba(255, 120, 120, 1)',
    spamReport: 'rgba(120, 180, 240, 1)',
    infoInput: 'rgba(46, 204, 113, 1)',
  };

  const pieData = {
    labels: pieLabels,
    datasets: [
      {
        data: hasInfoInput
          ? [
              currentStats.unread, 
              emailOpenedOnly, 
              currentStats.linkClicked, 
              currentStats.spamReport,
              currentStats.infoInput
            ]
          : [
              currentStats.unread, 
              emailOpenedOnly, 
              currentStats.linkClicked,
              currentStats.spamReport
            ],
        backgroundColor: hasInfoInput
          ? [
              pieChartColors.unread,
              pieChartColors.emailOpened,
              pieChartColors.linkClicked,
              pieChartColors.spamReport,
              pieChartColors.infoInput,
            ]
          : [
              pieChartColors.unread,
              pieChartColors.emailOpened,
              pieChartColors.linkClicked,
              pieChartColors.spamReport,
            ],
        borderColor: hasInfoInput
          ? [
              pieChartBorderColors.unread,
              pieChartBorderColors.emailOpened,
              pieChartBorderColors.linkClicked,
              pieChartBorderColors.spamReport,
              pieChartBorderColors.infoInput,
            ]
          : [
              pieChartBorderColors.unread,
              pieChartBorderColors.emailOpened,
              pieChartBorderColors.linkClicked,
              pieChartBorderColors.spamReport,
            ],
        borderWidth: 2,
      },
    ],
  };

  const doughnutData = {
    labels: pieLabels,
    datasets: [
      {
        data: hasInfoInput
          ? [
              currentStats.unread, 
              emailOpenedOnly, 
              currentStats.linkClicked, 
              currentStats.spamReport,
              currentStats.infoInput
            ]
          : [
              currentStats.unread, 
              emailOpenedOnly, 
              currentStats.linkClicked,
              currentStats.spamReport
            ],
        backgroundColor: hasInfoInput
          ? [
              pieChartColors.unread,
              pieChartColors.emailOpened,
              pieChartColors.linkClicked,
              pieChartColors.spamReport,
              pieChartColors.infoInput,
            ]
          : [
              pieChartColors.unread,
              pieChartColors.emailOpened,
              pieChartColors.linkClicked,
              pieChartColors.spamReport,
            ],
        borderColor: hasInfoInput
          ? [
              pieChartBorderColors.unread,
              pieChartBorderColors.emailOpened,
              pieChartBorderColors.linkClicked,
              pieChartBorderColors.spamReport,
              pieChartBorderColors.infoInput,
            ]
          : [
              pieChartBorderColors.unread,
              pieChartBorderColors.emailOpened,
              pieChartBorderColors.linkClicked,
              pieChartBorderColors.spamReport,
            ],
        borderWidth: 2,
      },
    ],
  };

  // 막대 그래프 옵션
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 0, // 범례를 그래프 상단에 최대한 가깝게
        bottom: 10,
        left: 10,
        right: 10,
      },
    },
    plugins: {
      legend: {
        position: 'top' as const,
        display: true,
        align: 'start' as const, // 범례를 왼쪽 정렬
        labels: {
          boxWidth: 12,
          padding: 4, // 범례 간격 줄이기
          font: {
            size: 11,
          },
          generateLabels: (chart: any) => {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original(chart);
            // 스팸을 제거하고 피싱을 스팸신고로 변경
            return labels
              .filter((label: any) => label.text !== '스팸')
              .map((label: any) => {
                if (label.text === '피싱') {
                  return { ...label, text: '스팸신고' };
                }
                return label;
              });
          },
        },
      },
      title: {
        display: false, // 제목 제거
      },
      datalabels: {
        anchor: (ctx: any) => {
          // 기본적으로 막대 밖에 표시, 차트 영역을 벗어나면 막대 안에 표시
          const chart = ctx.chart;
          const meta = chart.getDatasetMeta(ctx.datasetIndex);
          const bar = meta.data[ctx.dataIndex];
          if (!bar) return 'end';
          
          const yScale = chart.scales.y;
          const chartAreaTop = chart.chartArea.top;
          const chartAreaBottom = chart.chartArea.bottom;
          
          // 막대의 상단 위치 계산
          const barValue = ctx.dataset.data[ctx.dataIndex];
          if (barValue === 0) return 'end';
          
          const stackedValue = ctx.chart.data.datasets
            .slice(0, ctx.datasetIndex + 1)
            .reduce((sum: number, ds: any) => sum + (ds.data[ctx.dataIndex] || 0), 0);
          
          const barTopPixel = yScale.getPixelForValue(stackedValue);
          const barBottomPixel = yScale.getPixelForValue(stackedValue - barValue);
          
          // 텍스트 높이를 고려한 여유 공간 (약 50px)
          const textHeight = 50;
          
          // 차트 영역 상단을 벗어나면 막대 안에 표시
          if (barTopPixel < chartAreaTop + textHeight) {
            return 'center';
          }
          // 차트 영역 하단을 벗어나면 막대 안에 표시
          if (barBottomPixel > chartAreaBottom - 30) {
            return 'center';
          }
          return 'end';
        },
        align: (ctx: any) => {
          const chart = ctx.chart;
          const meta = chart.getDatasetMeta(ctx.datasetIndex);
          const bar = meta.data[ctx.dataIndex];
          if (!bar) return 'top';
          
          const yScale = chart.scales.y;
          const chartAreaTop = chart.chartArea.top;
          const chartAreaBottom = chart.chartArea.bottom;
          
          const barValue = ctx.dataset.data[ctx.dataIndex];
          if (barValue === 0) return 'top';
          
          const stackedValue = ctx.chart.data.datasets
            .slice(0, ctx.datasetIndex + 1)
            .reduce((sum: number, ds: any) => sum + (ds.data[ctx.dataIndex] || 0), 0);
          
          const barTopPixel = yScale.getPixelForValue(stackedValue);
          const barBottomPixel = yScale.getPixelForValue(stackedValue - barValue);
          
          const textHeight = 50;
          
          // 차트 영역을 벗어나면 막대 안에 표시
          if (barTopPixel < chartAreaTop + textHeight || barBottomPixel > chartAreaBottom - 30) {
            return 'center';
          }
          return 'top';
        },
        offset: (ctx: any) => {
          const chart = ctx.chart;
          const meta = chart.getDatasetMeta(ctx.datasetIndex);
          const bar = meta.data[ctx.dataIndex];
          if (!bar) return 10;
          
          const yScale = chart.scales.y;
          const chartAreaTop = chart.chartArea.top;
          const chartAreaBottom = chart.chartArea.bottom;
          
          const barValue = ctx.dataset.data[ctx.dataIndex];
          if (barValue === 0) return 0;
          
          const stackedValue = ctx.chart.data.datasets
            .slice(0, ctx.datasetIndex + 1)
            .reduce((sum: number, ds: any) => sum + (ds.data[ctx.dataIndex] || 0), 0);
          
          const barTopPixel = yScale.getPixelForValue(stackedValue);
          const barBottomPixel = yScale.getPixelForValue(stackedValue - barValue);
          
          const textHeight = 50;
          
          // 차트 영역을 벗어나면 막대 안에 표시 (offset 0)
          if (barTopPixel < chartAreaTop + textHeight || barBottomPixel > chartAreaBottom - 30) {
            return 0;
          }
          
          // 같은 막대(같은 dataIndex)에 여러 데이터셋이 있는지 확인하여 겹침 방지
          const datasetIndex = ctx.datasetIndex;
          const dataIndex = ctx.dataIndex;
          const allDatasets = ctx.chart.data.datasets;
          
          // 같은 막대에 있는 다른 데이터셋들의 개수 계산
          let activeDatasetsCount = 0;
          for (let i = 0; i < allDatasets.length; i++) {
            if (allDatasets[i].data[dataIndex] > 0) {
              activeDatasetsCount++;
            }
          }
          
          // 여러 데이터셋이 있으면 각각 다른 offset 적용 (스택막대 겹침 방지)
          if (activeDatasetsCount > 1) {
            // 현재 데이터셋의 순서에 따라 offset 조정
            let currentOrder = 0;
            for (let i = 0; i < datasetIndex; i++) {
              if (allDatasets[i].data[dataIndex] > 0) {
                currentOrder++;
              }
            }
            // 각 데이터셋마다 15px씩 간격을 둠
            return 10 + (currentOrder * 15);
          }
          
          return 10;
        },
        formatter: (value: number, ctx: any) => {
          // 값이 0이면 표시하지 않음
          if (value === 0) {
            return '';
          }
          
          // 전체(total)에 대한 비율 계산
          const total = currentStats.total || 1;
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          
          return `${value}\n(${percentage}%)`;
        },
        font: {
          size: 10,
          weight: 'bold' as const,
        },
        color: '#000', // 항상 검은색으로 통일
        padding: {
          top: 3,
          bottom: 3,
          left: 5,
          right: 5,
        },
        textStrokeColor: '#fff',
        textStrokeWidth: 2, // 텍스트 스트로크 두께 증가로 가독성 향상
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        stacked: true, // 스택막대 그래프
      },
      x: {
        stacked: true, // 스택막대 그래프
      },
    },
  };

  // 파이/도넛 차트 옵션
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false, // h3 제목이 있으므로 차트 내부 제목은 숨김
      },
      datalabels: {
        color: '#000', // 검정색으로 통일
        font: {
          size: 14,
          weight: 'bold' as const,
        },
        textStrokeColor: '#fff', // 흰색 음영
        textStrokeWidth: 2, // 음영 두께
        anchor: 'center' as const,
        align: 'center' as const,
        display: (ctx: any) => {
          // 5% 미만 슬라이스는 레이블 숨김 (범례와의 겹침 방지)
          const value = ctx.dataset.data[ctx.dataIndex];
          const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const percentage = total > 0 ? (value / total) * 100 : 0;
          return percentage >= 5;
        },
        formatter: (value: number, ctx: any) => {
          // 값이 0이면 표시하지 않음
          if (value === 0) {
            return null;
          }
          const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          return `${value}\n(${percentage}%)`;
        },
        clamp: true,
        clip: true,
        padding: {
          top: 3,
          bottom: 3,
          left: 5,
          right: 5,
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const value = context.parsed || context.raw;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value}명 (${percentage}%)`;
          },
        },
      },
    },
  };

  const statCards = [
    { label: '전체', value: currentStats.total, icon: '👥', accent: 'var(--legacy-accent, #3498db)' },
    { label: '미열람', value: currentStats.unread, icon: '📧', accent: '#8B5CF6' },
    { label: '메일열람', value: currentStats.emailOpened, icon: '📬', accent: '#F59E0B' },
    { label: '링크클릭', value: currentStats.linkClicked, icon: '🔗', accent: '#EF4444' },
    ...(hasInfoInput ? [{ label: '정보입력', value: currentStats.infoInput, icon: '📝', accent: '#10B981' }] : []),
  ];

  return (
    <div className="dashboard">
      <h1 className="dashboard-title">악성메일 모의 훈련 결과</h1>

      {/* 탭 메뉴 (대시보드 / 상세 데이터) — PDF 모드에서는 숨김 */}
      {!IS_PDF_MODE && (
        <div className="apt-tabs">
          <button
            className={`apt-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            대시보드
          </button>
          <button
            className={`apt-tab ${activeTab === 'detail' ? 'active' : ''}`}
            onClick={() => setActiveTab('detail')}
          >
            상세 데이터
          </button>
          <button
            className={`apt-tab ${activeTab === 'compare' ? 'active' : ''}`}
            onClick={() => setActiveTab('compare')}
          >
            데이터 비교
          </button>
        </div>
      )}

      {!IS_PDF_MODE && activeTab !== 'compare' && (
      <div className="filters">
        <div className="filter-group">
          <label htmlFor="round-select">회차</label>
          <select
            id="round-select"
            value={filters.round || ''}
            onChange={(e) => handleFilterChange('round', e.target.value || undefined)}
          >
            <option value="">선택하세요</option>
            {availableRounds.map((round) => (
              <option key={round} value={round}>
                {round}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="company-select">회사</label>
          <select
            id="company-select"
            value={filters.company || ''}
            onChange={(e) => handleFilterChange('company', e.target.value || undefined)}
          >
            <option value="">전체</option>
            {availableCompanies.map((company) => (
              <option key={company} value={company}>
                {company}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="team-select">팀</label>
          <select
            id="team-select"
            value={filters.team || ''}
            onChange={(e) => handleFilterChange('team', e.target.value || undefined)}
            disabled={!filters.company}
          >
            <option value="">전체</option>
            {availableTeams.map((team) => (
              <option key={team} value={team}>
                {team}
              </option>
            ))}
          </select>
        </div>

        {/* 대시보드 탭: PDF 다운로드 / 상세 데이터 탭: 엑셀은 테이블 내부 버튼 사용 */}
        {activeTab === 'dashboard' && !IS_PDF_MODE && (
          <div className="filter-group">
            <button
              className="export-button"
              onClick={handleDownloadPdf}
              disabled={isLoading || !stats || isDownloadingPdf}
            >
              {isDownloadingPdf ? 'PDF 생성 중...' : 'PDF 다운로드'}
            </button>
          </div>
        )}
      </div>
      )}

      {activeTab === 'compare' ? (
        <div className="tab-panel">
          <AptCompareView
            isPdfMode={IS_PDF_MODE && PDF_TAB === 'compare'}
            initialCompany={IS_PDF_MODE ? pdfUrlParams.get('company') || '' : filters.company || ''}
          />
        </div>
      ) : activeTab === 'detail' ? (
        <div className="tab-panel">
          <AptDetailTable filters={filters} titleForFile={getChartTitle()} />
        </div>
      ) : isLoading ? (
        <div className="loading">데이터를 불러오는 중...</div>
      ) : (
        <div className="tab-panel">
          {/* AdminLTE small-box 스타일 통계 카드 */}
          <div className="stats-summary">
            {statCards.map((card) => (
              <div className="stat-card" key={card.label}>
                <div className="stat-card-body">
                  <div className="stat-card-info">
                    <div className="stat-label">{card.label}</div>
                    <div className="stat-value">{card.value}</div>
                  </div>
                  <div className="stat-card-icon" style={{ background: `${card.accent}20`, color: card.accent }}>
                    {card.icon}
                  </div>
                </div>
                <div className="stat-card-footer" style={{ background: card.accent }} />
              </div>
            ))}
          </div>

          {/* 차트 카드 */}
          <div className="charts-container">
            <div className="chart-card">
              <div className="chart-card-header">
                <h3 style={{ fontSize: getTitleFontSize() }}>{getChartTitle()}</h3>
              </div>
              <div className="chart-card-body">
                <div className="chart-wrapper">
                  <Bar data={barData} options={barOptions} plugins={[ChartDataLabels]} />
                </div>
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-card-header">
                <h3 style={{ fontSize: getTitleFontSize() }}>{getChartTitle()}</h3>
              </div>
              <div className="chart-card-body">
                <div className="chart-wrapper">
                  <Pie data={pieData} options={pieOptions} plugins={[ChartDataLabels]} />
                </div>
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-card-header">
                <h3 style={{ fontSize: getTitleFontSize() }}>{getChartTitle()}</h3>
              </div>
              <div className="chart-card-body">
                <div className="chart-wrapper">
                  <Doughnut data={doughnutData} options={pieOptions} plugins={[ChartDataLabels]} />
                </div>
              </div>
            </div>
          </div>

          {/* 스팸신고자 테이블 */}
          {!isLoading && (
            <div className="spam-reporters-section">
              <div className="section-title">
                <span>⚠️</span>
                <span>스팸신고자</span>
              </div>
              {isLoadingSpamReporters ? (
                <div className="loading">데이터를 불러오는 중...</div>
              ) : spamReporters.length > 0 ? (
                <div className="themed-table-wrap">
                  <table className="themed-table">
                    <thead>
                      <tr>
                        {([
                          ['company', '회사'],
                          ['team', '팀'],
                          ['name', '이름'],
                          ['category', '카테고리분류'],
                        ] as [SpamSortKey, string][]).map(([key, label]) => (
                          <th
                            key={key}
                            onClick={() => handleSpamSort(key)}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            title="클릭하여 정렬"
                          >
                            {label}
                            <span style={{ opacity: key === spamSortKey ? 1 : 0.4, fontSize: '0.75rem' }}>
                              {spamSortIndicator(key)}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedSpamReporters.map((reporter, index) => (
                        <tr key={index}>
                          <td>{reporter.company || '-'}</td>
                          <td>{reporter.team || '-'}</td>
                          <td>{reporter.name || '-'}</td>
                          <td>
                            <span style={{
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              background: reporter.category === '피싱' ? 'rgba(139,92,246,0.12)' : 'rgba(245,158,11,0.12)',
                              color: reporter.category === '피싱' ? '#7C3AED' : '#D97706',
                            }}>
                              {reporter.category || '-'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-data">스팸신고자가 없습니다.</div>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default Dashboard;

